import React, { useState, useEffect, useMemo, useRef } from 'react';

import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  UserOutlined,
  InboxOutlined,
  SearchOutlined,
  StopOutlined
} from '@ant-design/icons';
import { formatDateTime } from '@utils/dateTime';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { Card, Table, Button, Space, Modal, Spin, message, Alert, Tooltip, Row, Col, Statistic, Input, Divider, Progress, Typography, Tag, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FulfillmentStatusLabel from '@shared/fulfillments/FulfillmentStatusLabel';

const { Text } = Typography;

const MergeFulfillments = ({
  fulfillmentService,
  role = 'admin',
  merchantId = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mergeableGroups, setMergeableGroups] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedItemsMap, setSelectedItemsMap] = useState({});

  // 批量合併狀態
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [isBatchMerging, setIsBatchMerging] = useState(false);
  const [batchCancelled, setBatchCancelled] = useState(false);
  const cancelRef = useRef(false);
  const [batchProgress, setBatchProgress] = useState({
    total: 0,
    current: 0,
    successful: 0,
    failed: 0,
    currentGroup: null,
    failedGroups: []
  });

  // 載入可合併的出貨單分組
  useEffect(() => {
    loadMergeableGroups();
  }, [merchantId]);

  const loadMergeableGroups = async () => {
    setLoading(true);
    try {
      const params = merchantId ? { merchant_id: merchantId } : {};
      const data = await fulfillmentService.getMergeableGroups(params);

      if (data.success) {
        const groups = data.data || [];
        setMergeableGroups(groups);
        setExpandedRowKeys(groups.map((_, idx) => idx) || []);

        // 初始化每個分組的選中項目 (預設全選)
        const newSelectedItemsMap = {};
        groups.forEach(group => {
          const items = group.fulfillments || [];
          if (group.group_key) {
            newSelectedItemsMap[group.group_key] = items.map(i => i.id);
          }
        });
        setSelectedItemsMap(newSelectedItemsMap);
      } else {
        message.error(t('common.error.load_failed'));
      }
    } catch (error) {
      if (error.name === 'NetworkError') {
        handleNetworkError(error);
      } else {
        handleApiError(error, t('common.error.load_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 處理合併
  const handleMerge = async (group, specificIds = null) => {
    const allItems = group.fulfillments || [];
    const selectedIds = specificIds || selectedItemsMap[group.group_key] || allItems.map(i => i.id);

    if (selectedIds.length < 2) {
      message.warning(t('shared.orders.merge.select_at_least_two'));
      return;
    }

    const itemsToMerge = allItems.filter(item => selectedIds.includes(item.id));

    Modal.confirm({
      title: t('shared.orders.merge.confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>{t('shared.orders.merge.confirm_message', { count: selectedIds.length })}</p>
          <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {itemsToMerge.map(item => (
              <li key={item.id}>{item.number}</li>
            ))}
          </ul>
        </div>
      ),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await fulfillmentService.merge(selectedIds);

          if (data.success) {
            message.success(t('shared.orders.merge.success'));
            // 重新拉清單而非 local filter：部分合併（一組只勾部分 fulfillment）後，
            // 剩餘的 fulfillment + 合併後新單仍可能還可以合併，本地 filter 會誤把整組砍光，
            // 變成「無待合併訂單」直到使用者按重新整理才更新。
            setSelectedItemsMap(prev => {
              const newMap = { ...prev };
              delete newMap[group.group_key];
              return newMap;
            });
            await loadMergeableGroups();
          } else {
            message.error(data.message || t('shared.orders.merge.failed'));
          }
        } catch (error) {
          if (error.name === 'NetworkError') {
            handleNetworkError(error);
          } else {
            handleApiError(error, t('shared.orders.merge.failed'));
          }
        }
      }
    });
  };

  // 批量合併
  const handleBatchMerge = () => {
    const groupsToMerge = mergeableGroups.filter(group => {
      const selectedIds = selectedItemsMap[group.group_key] || [];
      return selectedIds.length >= 2;
    });

    if (groupsToMerge.length === 0) {
      message.warning(t('shared.orders.merge.no_selected_for_batch_merge'));
      return;
    }

    setBatchModalVisible(true);
    setIsBatchMerging(false);
    setBatchCancelled(false);
    cancelRef.current = false;
    setBatchProgress({
      total: groupsToMerge.length,
      current: 0,
      successful: 0,
      failed: 0,
      currentGroup: null,
      failedGroups: []
    });
  };

  // 開始批量合併
  const startBatchMerge = async () => {
    cancelRef.current = false;
    setBatchCancelled(false);
    setIsBatchMerging(true);
    await executeBatchMerge();
  };

  // 取消批量合併
  const handleCancelBatchMerge = () => {
    cancelRef.current = true;
    setBatchCancelled(true);
  };

  // 執行批量合併
  const executeBatchMerge = async () => {
    const selectedGroups = mergeableGroups.filter(group => {
      const selectedIds = selectedItemsMap[group.group_key] || [];
      return selectedIds.length >= 2;
    });
    const total = selectedGroups.length;

    setIsBatchMerging(true);
    setBatchProgress({
      total,
      current: 0,
      successful: 0,
      failed: 0,
      currentGroup: null,
      failedGroups: []
    });

    const failedGroups = [];
    let successCount = 0;

    for (let i = 0; i < selectedGroups.length; i++) {
      // 檢查是否已取消
      if (cancelRef.current) {
        message.info(t('shared.orders.merge.batch_cancelled', { done: successCount, remaining: total - i }));
        break;
      }

      const group = selectedGroups[i];

      setBatchProgress(prev => ({
        ...prev,
        current: i + 1,
        currentGroup: group
      }));

      try {
        const ids = selectedItemsMap[group.group_key] || [];

        if (ids.length < 2) {
          throw new Error(t('shared.orders.merge.select_at_least_two'));
        }

        const data = await fulfillmentService.merge(ids);

        if (data.success) {
          successCount++;
          setBatchProgress(prev => ({
            ...prev,
            successful: successCount
          }));
        } else {
          failedGroups.push({ group, error: data.message || 'Unknown error' });
          setBatchProgress(prev => ({
            ...prev,
            failed: failedGroups.length,
            failedGroups
          }));
        }
      } catch (error) {
        failedGroups.push({ group, error: error.message || 'Network error' });
        setBatchProgress(prev => ({
          ...prev,
          failed: failedGroups.length,
          failedGroups
        }));
      }

      if (i < selectedGroups.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    if (!cancelRef.current && failedGroups.length === 0 && successCount > 0) {
      message.success(t('shared.orders.merge.batch_all_success', { count: successCount }));
    }
  };

  // 過濾分組（根據關鍵字搜尋）
  const filteredGroups = useMemo(() => {
    if (!searchKeyword.trim()) {
      return mergeableGroups;
    }

    const keyword = searchKeyword.toLowerCase().trim();

    return mergeableGroups.filter(group => {
      if (group.receiver_name?.toLowerCase().includes(keyword)) return true;
      if (group.phone?.toLowerCase().includes(keyword)) return true;
      if (group.channel_name?.toLowerCase().includes(keyword)) return true;
      if (group.shipping_type_name?.toLowerCase().includes(keyword)) return true;

      const items = group.fulfillments || [];
      if (items.some(item => item.number?.toLowerCase().includes(keyword))) return true;

      return false;
    });
  }, [mergeableGroups, searchKeyword]);

  // 計算統計資訊
  const statistics = useMemo(() => {
    if (!filteredGroups || filteredGroups.length === 0) {
      return { totalGroups: 0, totalItemsCount: 0, totalQuantity: 0 };
    }

    return {
      totalGroups: filteredGroups.length,
      totalItemsCount: filteredGroups.reduce((sum, group) => sum + (group.fulfillment_count || 0), 0),
      totalQuantity: filteredGroups.reduce((sum, group) => sum + group.total_items, 0)
    };
  }, [filteredGroups]);

  const totalItemsSelectedForMerge = useMemo(() => {
    let count = 0;
    Object.values(selectedItemsMap).forEach(ids => {
      if (ids.length >= 2) {
        count += ids.length;
      }
    });
    return count;
  }, [selectedItemsMap]);

  const handleSelectAll = () => {
    const newSelectedItemsMap = {};
    mergeableGroups.forEach(group => {
      const items = group.fulfillments || [];
      if (group.group_key) {
        newSelectedItemsMap[group.group_key] = items.map(i => i.id);
      }
    });
    setSelectedItemsMap(newSelectedItemsMap);
  };

  const handleClearSelection = () => {
    setSelectedItemsMap({});
  };

  // 出貨單詳情欄位
  const fulfillmentColumns = [
    {
      title: t('shared.orders.merge.columns.fulfillment_number'),
      dataIndex: 'number',
      key: 'number',
      width: 180,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: t('shared.orders.merge.columns.warehouse'),
      dataIndex: 'warehouse_name',
      key: 'warehouse_name',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: t('shared.orders.merge.columns.related_orders'),
      dataIndex: 'order_numbers',
      key: 'order_numbers',
      render: (orders) => (
        <Space size="small" wrap>
          {orders.map(o => <Tag key={o}>{o}</Tag>)}
        </Space>
      )
    },
    {
      title: t('models.fulfillment.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <FulfillmentStatusLabel status={status} />,
    },
    {
      title: t('common.total_quantity'),
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      width: 100,
      align: 'right'
    },
    {
      title: t('shared.orders.detail.fulfillments.split.amount', { defaultValue: '金額' }),
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (_, record) => (
        record.total_amount != null
          ? <Text>{Number(record.total_amount).toFixed(2)} {record.currency}</Text>
          : <Text type="secondary">—</Text>
      )
    }
  ];

  // 渲染每個合併分組
  const renderMergeGroup = (group, index) => {
    const isExpanded = expandedRowKeys.includes(index);
    const selectedItemIds = selectedItemsMap[group.group_key] || [];

    // 合併後預覽金額：選中的 fulfillments 的 total_amount 直接 sum（合併後 backend 也是這樣算，無歧義）
    const selectedFulfillments = group.fulfillments.filter(f => selectedItemIds.includes(f.id));
    const mergedAmount = selectedFulfillments.reduce((sum, f) => sum + Number(f.total_amount || 0), 0);
    const mergedCurrency = selectedFulfillments[0]?.currency;

    const rowSelection = {
      selectedRowKeys: selectedItemIds,
      onChange: (selectedRowKeys) => {
        setSelectedItemsMap(prev => ({
          ...prev,
          [group.group_key]: selectedRowKeys
        }));
      }
    };

    return (
      <Card
        key={group.group_key}
        hoverable
        style={{
          marginBottom: 24,
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          transition: 'all 0.3s',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* 分組標題區 */}
        <div
          style={{
            padding: '16px 20px',
            background: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
            cursor: 'pointer',
            transition: 'background 0.3s'
          }}
          onClick={() => {
            if (isExpanded) {
              setExpandedRowKeys(expandedRowKeys.filter(key => key !== index));
            } else {
              setExpandedRowKeys([...expandedRowKeys, index]);
            }
          }}
        >
          <Row align="middle" gutter={24}>
            <Col flex="none" style={{ minWidth: 280 }}>
              <Space direction="vertical" size={2}>
                <Text style={{ fontSize: 16, fontWeight: 600 }}>
                  <UserOutlined style={{ marginRight: 6 }} />
                  {group.receiver_name}
                </Text>
                <Text style={{ fontSize: 13, color: '#999' }}>
                  {group.phone} · {group.full_address}
                </Text>
              </Space>
            </Col>

            <Col flex="none">
              <Space size={8} wrap>
                <Tag color="cyan" style={{ margin: 0 }}>
                  {t('models.sales_channel._')}：{group.channel_name}
                </Tag>
                <Tag color="geekblue" style={{ margin: 0 }}>
                  {t('models.shipping_type._')}：{group.shipping_type_name}
                </Tag>
                {group.warehouse_name && (
                  <Tag color="purple" style={{ margin: 0 }}>
                    {t('models.warehouse._')}：{group.warehouse_name}
                  </Tag>
                )}
              </Space>
            </Col>

            <Col flex="none">
              <div style={{ width: 1, height: 28, background: '#e8e8e8', margin: '0 16px' }} />
            </Col>

            <Col flex="none">
              <Space size={20}>
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                    {t('shared.orders.merge.fulfillment_count')}
                  </div>
                  <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                    {group.fulfillment_count}
                  </Text>
                </div>
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{t('shared.orders.merge.items_count')}</div>
                  <Text strong style={{ fontSize: 18, color: '#faad14' }}>
                    {group.total_items}
                  </Text>
                </div>
                {selectedItemIds.length >= 2 && (
                  <div style={{ textAlign: 'center', minWidth: 100 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                      {t('shared.orders.merge.merged_amount', { defaultValue: '合併後金額' })}
                    </div>
                    <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                      {mergedAmount.toFixed(2)} {mergedCurrency}
                    </Text>
                  </div>
                )}
              </Space>
            </Col>

            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                disabled={selectedItemIds.length < 2}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMerge(group, selectedItemIds);
                }}
              >
                {t('shared.orders.merge.actions.merge')} {selectedItemIds.length > 0 && `(${selectedItemIds.length})`}
              </Button>
            </Col>
          </Row>
        </div>

        {/* 展開的出貨單列表 */}
        {isExpanded && (
          <div style={{ padding: 20, background: '#fff' }}>
            <div style={{ marginBottom: 12, paddingLeft: 4 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t('shared.orders.merge.group_summary', {
                  count: group.fulfillment_count,
                  type: t('shared.orders.merge.type_fulfillment')
                })}
              </Text>
            </div>
            <Table
              rowSelection={rowSelection}
              columns={fulfillmentColumns}
              dataSource={group.fulfillments}
              pagination={false}
              rowKey="id"
              size="small"
              bordered
              style={{
                borderRadius: 4,
                overflow: 'hidden'
              }}
            />
          </div>
        )}
      </Card>
    );
  };

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 統計 + 操作按鈕 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          {mergeableGroups.length > 0 ? (
            <Space size="large">
              <Tooltip
                title={
                  <div>
                    <div>{t('shared.orders.merge.description')}</div>
                    <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>{t('shared.orders.merge.max_groups_hint', { max: 20 })}</div>
                  </div>
                }
              >
                <ExclamationCircleOutlined style={{ color: '#1890ff', fontSize: 16, cursor: 'help' }} />
              </Tooltip>
              <Statistic
                title={t('shared.orders.merge.stats.mergeable_groups')}
                value={statistics.totalGroups}
                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ fontSize: 20 }}
              />
              <Statistic
                title={t('shared.orders.merge.stats.pending_items')}
                value={statistics.totalItemsCount}
                prefix={<ShoppingOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ fontSize: 20 }}
              />
              <Statistic
                title={t('shared.orders.merge.stats.total_products')}
                value={statistics.totalQuantity}
                prefix={<InboxOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontSize: 20 }}
              />
            </Space>
          ) : <span />}
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadMergeableGroups}
              loading={loading}
            >
              {t('common.button.refresh')}
            </Button>
            {mergeableGroups.length > 0 && (
              <Button
                type="primary"
                danger
                icon={<ThunderboltOutlined />}
                onClick={handleBatchMerge}
                disabled={isBatchMerging || totalItemsSelectedForMerge < 2}
              >
                {t('shared.orders.merge.batch_merge')} {totalItemsSelectedForMerge > 0 && `(${totalItemsSelectedForMerge})`}
              </Button>
            )}
          </Space>
        </div>

          <Spin spinning={loading}>
            {mergeableGroups.length === 0 ? (
              <Card>
                <Empty
                  description={t('shared.orders.merge.no_groups')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </Card>
            ) : (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <Input
                    placeholder={t('shared.orders.merge.search_placeholder')}
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    allowClear
                    size="large"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>

                <div style={{
                  marginBottom: 16,
                  padding: '12px 16px',
                  background: '#fafafa',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Space>
                    <Text type="secondary">{t('shared.orders.merge.quick_select')}</Text>
                    <Button size="small" onClick={handleSelectAll}>
                      {t('common.button.select_all')}
                    </Button>
                    <Button size="small" onClick={handleClearSelection}>
                      {t('common.button.clear_all')}
                    </Button>
                    <Divider type="vertical" />
                    <Button
                      size="small"
                      onClick={() => setExpandedRowKeys(filteredGroups.map((_, idx) => idx))}
                    >
                      {t('shared.orders.merge.actions.expand_all')}
                    </Button>
                    <Button size="small" onClick={() => setExpandedRowKeys([])}>
                      {t('shared.orders.merge.actions.collapse_all')}
                    </Button>
                  </Space>
                  <Text type="secondary">
                    {searchKeyword ? (
                      t('shared.orders.merge.search_result', { count: filteredGroups.length })
                    ) : (
                      t('shared.orders.merge.total_groups', { count: mergeableGroups.length })
                    )}
                  </Text>
                </div>

                {filteredGroups.length === 0 ? (
                  <Card>
                    <Empty
                      description={t('shared.orders.merge.no_search_results')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                ) : (
                  <div>
                    {filteredGroups.map((group) => {
                      const index = mergeableGroups.findIndex(g => g === group);
                      return renderMergeGroup(group, index);
                    })}
                  </div>
                )}
              </div>
            )}
          </Spin>
      </Space>

      {/* 批量合併 Modal */}
      <Modal
        title={isBatchMerging ? t('shared.orders.merge.batch_progress_title') : t('shared.orders.merge.batch_confirm_title')}
        open={batchModalVisible}
        onCancel={() => {
          if (!isBatchMerging) {
            setBatchModalVisible(false);
          }
        }}
        closable={!isBatchMerging}
        maskClosable={false}
        width={600}
        footer={
          isBatchMerging ? (
            batchProgress.current < batchProgress.total && !batchCancelled ? (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={handleCancelBatchMerge}
              >
                {t('shared.orders.merge.batch_cancel')}
              </Button>
            ) : null
          ) : (
            <Space>
              <Button onClick={() => setBatchModalVisible(false)}>
                {t('common.button.cancel')}
              </Button>
              <Button
                type="primary"
                danger
                onClick={startBatchMerge}
                icon={<ThunderboltOutlined />}
                disabled={isBatchMerging || totalItemsSelectedForMerge < 2}
              >
                {t('common.button.confirm')}
              </Button>
            </Space>
          )
        }
      >
        {!isBatchMerging ? (
          <div>
            <p>{t('shared.orders.merge.batch_confirm_message', { count: batchProgress.total })}</p>
            <Alert
              message={t('shared.orders.merge.batch_confirm_warning')}
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          </div>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>
                  {t('shared.orders.merge.progress_overall')}:
                  {' '}
                  {batchProgress.current} / {batchProgress.total}
                </Text>
              </div>
              <Progress
                percent={Math.round((batchProgress.current / batchProgress.total) * 100)}
                status={batchCancelled ? 'exception' : batchProgress.current === batchProgress.total ? 'success' : 'active'}
                strokeColor={batchCancelled ? '#faad14' : {
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
            </div>

            {batchCancelled && (
              <Alert
                message={t('shared.orders.merge.batch_cancelled_title')}
                description={t('shared.orders.merge.batch_cancelled', { done: batchProgress.successful, remaining: batchProgress.total - batchProgress.current })}
                type="warning"
                showIcon
              />
            )}

            {!batchCancelled && batchProgress.currentGroup && batchProgress.current < batchProgress.total && (
              <Alert
                message={t('shared.orders.merge.processing_group')}
                description={
                  <div>
                    <div>
                      <strong>{t('shared.orders.merge.receiver_info')}:</strong>{' '}
                      {batchProgress.currentGroup.receiver_name} ({batchProgress.currentGroup.phone})
                    </div>
                    <div>
                      <strong>{t('shared.orders.merge.fulfillment_count')}:</strong>{' '}
                      {batchProgress.currentGroup.fulfillment_count} {t('shared.orders.merge.type_fulfillment')}
                    </div>
                  </div>
                }
                type="info"
                showIcon
              />
            )}

            <div style={{ display: 'flex', gap: 16 }}>
              <Tag color="success" style={{ fontSize: '14px', padding: '4px 12px' }}>
                <CheckCircleOutlined /> {t('shared.orders.merge.successful')}: {batchProgress.successful}
              </Tag>
              {batchProgress.failed > 0 && (
                <Tag color="error" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  <ExclamationCircleOutlined /> {t('shared.orders.merge.failed_count')}: {batchProgress.failed}
                </Tag>
              )}
            </div>

            {batchProgress.failedGroups.length > 0 && (
              <Alert
                message={t('shared.orders.merge.failed_groups')}
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {batchProgress.failedGroups.map((item, idx) => (
                      <li key={idx}>
                        {item.group.receiver_name} - {item.group.fulfillment_count} {t('shared.orders.merge.type_fulfillment')}
                        <br />
                        <span style={{ fontSize: '12px', color: '#999' }}>
                          {item.error}
                        </span>
                      </li>
                    ))}
                  </ul>
                }
                type="error"
                showIcon
              />
            )}

            {(batchProgress.current === batchProgress.total || batchCancelled) && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setBatchModalVisible(false);
                    setIsBatchMerging(false);
                    setBatchCancelled(false);
                    cancelRef.current = false;
                  }}>
                    {t('common.button.close')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setBatchModalVisible(false);
                      setIsBatchMerging(false);
                      setBatchCancelled(false);
                      cancelRef.current = false;
                      loadMergeableGroups();
                    }}
                  >
                    {t('shared.orders.merge.load_more')}
                  </Button>
                </Space>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </>
  );
};

export default MergeFulfillments;
